import * as _ from 'lodash';
import moment from 'moment';

import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { Item } from 'fc-ui-components';

import { LanguageService } from 'services';
import { SeasonName, seasonTranslationKeyMap, FileType } from 'helpers';

const now = moment();

export class MetadataUiMapper {
    private readonly expirtyDateFilterOptions = [
        { text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_A_MONTH' }, value: moment(now).add(1, 'M') },
        {
            text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_X_MONTHS', placeholder: { months: 3 } },
            value: moment(now).add(3, 'M')
        },
        {
            text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_X_MONTHS', placeholder: { months: 6 } },
            value: moment(now).add(6, 'M')
        },
        {
            text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_X_MONTHS', placeholder: { months: 9 } },
            value: moment(now).add(9, 'M')
        },
        { text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_A_YEAR' }, value: moment(now).add(1, 'y') },
        {
            text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_X_YEARS', placeholder: { years: 2 } },
            value: moment(now).add(2, 'y')
        },
        {
            text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_X_YEARS', placeholder: { years: 4 } },
            value: moment(now).add(4, 'y')
        },
        {
            text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_MORE_THAN_X_YEARS', placeholder: { years: 6 } },
            value: moment(now).add(6, 'y')
        }
    ];
    private readonly marketingChannelsKeyMap = {
        [ChannelName.Digitalsignage]: 'MARKETING_CHANNELS.DIGITALSIGNAGE',
        [ChannelName.Ecommerce]: 'MARKETING_CHANNELS.ECOMMERCE',
        [ChannelName.Newsletter]: 'MARKETING_CHANNELS.NEWSLETTER',
        [ChannelName.Print]: 'MARKETING_CHANNELS.PRINT',
        [ChannelName.Socialmedia]: 'MARKETING_CHANNELS.SOCIALMEDIA',
        [ChannelName.Thirdparty]: 'MARKETING_CHANNELS.THIRDPARTY',
        [ChannelName.Web]: 'MARKETING_CHANNELS.WEB'
    };
    private readonly marketingCategoryKeyMap = {
        [FileType.Image]: 'FILE_TYPES.IMAGE',
        [FileType.Video]: 'FILE_TYPES.VIDEO',
        [FileType.Document]: 'FILE_TYPES.DOCUMENT',
        [FileType.File]: 'FILE_TYPES.OTHER'
    };

    constructor(private languageService: LanguageService) {}

    public mapMetadataToSeasonFilter(
        marketingFilesSeasons: MarketingFileSeason[],
        selectedSeasons: MarketingFileSeason[]
    ): Observable<Item[]> {
        const seasons = _.chain(marketingFilesSeasons)
            .uniqWith(_.isEqual)
            .orderBy(['year', 'season'], ['asc', 'desc'])
            .value();

        const seasonItems: Observable<Item>[] = _.map(seasons, (season) =>
            this.mapSeasonToItem(season, selectedSeasons)
        );

        return forkJoin(seasonItems);
    }

    public mapMetadataToExpiryDate(marketingFilesExpiryDates: string[]): Observable<Item[]> {
        const latestExpirationDate = moment(_.max(_.uniq(marketingFilesExpiryDates)));

        const allDatesOption = { text: { key: 'BRAND_PROMO_IMAGE_EXPIRY_ALL' }, value: 'ALL' };
        const expiryDateOptions = _.filter(this.expirtyDateFilterOptions, (expiryDate) =>
            moment(expiryDate.value).isSameOrBefore(latestExpirationDate)
        );

        const expiryDateItems: Observable<Item>[] = _.map([allDatesOption, ...expiryDateOptions], (expiryDate) =>
            this.maptExpiryDateToItem(expiryDate)
        );

        return forkJoin(expiryDateItems);
    }

    public mapMetadataToChannelsFilter(
        marketingFilesChannels: ChannelName[],
        selectedChannels: ChannelName[]
    ): Observable<Item[]> {
        const channels: ChannelName[] = _.chain(marketingFilesChannels).flatten().uniq().orderBy().value();

        const channelItems: Observable<Item>[] = _.map(channels, (channel) =>
            this.mapChannelToItem(channel, selectedChannels)
        );

        return forkJoin(channelItems);
    }

    public mapMetadataToFileTypesFilter(
        marketingFileTypes: FileType[],
        selectedFileTypes: FileType[]
    ): Observable<Item[]> {
        const categories = _.uniq(marketingFileTypes);

        const categoryItems: Observable<Item>[] = _.map(categories, (category) =>
            this.mapFileTypeToItem(category, selectedFileTypes)
        );

        return forkJoin(categoryItems);
    }

    private mapFileTypeToItem(fileType: FileType, selectedFileTypes: FileType[]): Observable<Item> {
        return this.languageService.translateObservable(this.marketingCategoryKeyMap[fileType]).pipe(
            map((fileTypeName) => ({
                text: fileTypeName,
                label: fileTypeName,
                value: fileType,
                checked: _.includes(selectedFileTypes, fileType)
            }))
        );
    }

    private mapChannelToItem(channel: ChannelName, selectedChannels: ChannelName[]): Observable<Item> {
        return this.languageService.translateObservable(this.marketingChannelsKeyMap[channel]).pipe(
            map((channelName) => ({
                text: channelName,
                label: channelName,
                value: channel,
                checked: _.includes(selectedChannels, channel)
            }))
        );
    }

    private maptExpiryDateToItem(expiryDateOption: ExpiryDateOption): Observable<Item> {
        const placeholder = expiryDateOption.text.placeholder ? expiryDateOption.text.placeholder : null;
        return this.languageService.translateObservable(expiryDateOption.text.key, placeholder).pipe(
            map((expiryDateName) => ({
                text: expiryDateName,
                label: expiryDateName,
                value: expiryDateOption.value
            }))
        );
    }

    private mapSeasonToItem(season: MarketingFileSeason, selectedSeasons: MarketingFileSeason[]): Observable<Item> {
        const year = season.year || '';
        return this.languageService.translateObservable(seasonTranslationKeyMap[season.season]).pipe(
            map((seasonName) => ({
                text: `${seasonName} ${year}`,
                label: `${seasonName} ${year}`,
                value: season,
                checked: !!_.find(selectedSeasons, season)
            }))
        );
    }
}

export interface MarketingFileSeason {
    season: SeasonName;
    year: number;
}

export enum ChannelName {
    Digitalsignage = 'digitalsignage',
    Ecommerce = 'ecommerce',
    Newsletter = 'newsletter',
    Print = 'print',
    Socialmedia = 'socialmedia',
    Thirdparty = 'thirdparty',
    Web = 'web'
}

interface ExpiryDateOption {
    text: {
        key: string;
        placeholder?: { months?: number; years?: number };
    };
    value: moment.Moment | string;
}
